import {
  Box,
  Icon,
  SkeletonGroup,
  Switch,
  Text,
} from "@salestrak/ui";
import { useState } from "react";
import type { Omittable } from "@salestrak/ui";
import type { ObjectAccessLevel } from "#app/lib/data";
import { EntityTag } from "#app/components/entity";
import type {
  AccessSummaryMember,
  AccessSummaryTeam,
  TeamMembersSource,
} from "#app/lib/hooks/use-object-sharing";
import { useTeamAccessMembers } from "#app/lib/hooks/use-team-access-members";
import { memberCountLabel } from "#app/lib/utils/member-count";
import { AccessLevelMenu } from "./access-level-menu";
import {
  ACCESS_ROW_HEIGHT,
  ACCESS_VISUAL_PX,
  AccessRowFrame,
} from "./access-row";
import {
  memberEntity,
  teamEntity,
  roleBadge,
  memberMeta,
  LockedLevel,
  NESTED_ROW_HEIGHT,
} from "./share-rows";
import { SkeletonRow } from "./share-dialog-skeleton";

export function TeamAccessRow({
  team,
  source,
  memberLabel,
  onChange,
  onToggle,
  onMemberChange,
  onMemberDeny,
  onMemberReset,
}: Omittable<{
  team: AccessSummaryTeam;
  source: TeamMembersSource;
  memberLabel: (m: AccessSummaryMember) => string;
  onChange: (level: ObjectAccessLevel) => void;
  onToggle: (on: boolean) => void;
  onMemberChange: (id: string, level: ObjectAccessLevel) => void;
  onMemberDeny: (id: string, level: ObjectAccessLevel) => void;
  onMemberReset: (id: string) => void;
}>) {
  const [expanded, setExpanded] = useState(false);
  // A team on the item's Teams property is granted by the property, not by
  // a share: its switch cannot take the access away, so it does not offer to.
  const onProperty = team.source === "team";
  return (
    <>
      <Box direction="row" align="center" gap="base" height={ACCESS_ROW_HEIGHT}>
        {/* The row's leading region is the disclosure: press anywhere on the
            team to open its roster. The controls stay outside it so two
            presses never nest. The wash bleeds a step left of the tile
            and pads it by the same amount, so the tile keeps the member
            rows' rail while the hover reads centred on it. */}
        <Box
          as="button"
          type="button"
          direction="row"
          align="center"
          gap="base"
          flex={1}
          minWidth={0}
          height={ACCESS_ROW_HEIGHT - 4}
          paddingLeft="xs"
          paddingRight="sm"
          marginLeftNegative="xs"
          rounded="sm"
          hoverBackground
          cursor="pointer"
          aria-label={expanded ? "Hide members" : "Show members"}
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          <EntityTag
            entity={teamEntity(team)}
            display="visual"
            size="sm"
            visualBoxSize={ACCESS_VISUAL_PX}
            interactive={false}
          />
          <Box flex={1} minWidth={0} direction="row" align="center" gap="sm">
            <Text size="sm" truncate={1} variant={team.enabled ? undefined : "muted"}>
              {team.name}
            </Text>
            <Text size="xs" variant="muted" truncate={1}>
              {onProperty
                ? `${memberCountLabel(team.memberCount)} · On this Project`
                : memberCountLabel(team.memberCount)}
            </Text>
            <Icon
              name={expanded ? "chevron-down" : "chevron-right"}
              size="sm"
              color="faint"
            />
          </Box>
        </Box>
        {/* The same control slot every row frame ends in, so the team's
            menu and switch line up with its members' below. */}
        <Box direction="row" align="center" gap="sm" shrink={false}>
          <AccessLevelMenu
            value={team.accessLevel}
            disabled={!team.enabled}
            onChange={onChange}
          />
          <Switch
            aria-label={`${team.name} has access`}
            checked={team.enabled}
            disabled={onProperty}
            onCheckedChange={onToggle}
            size="sm"
          />
        </Box>
      </Box>
      {expanded ? (
        <TeamMemberRows
          source={source}
          teamId={team.id}
          memberCount={team.memberCount}
          memberLabel={memberLabel}
          onChange={onMemberChange}
          onDeny={onMemberDeny}
          onReset={onMemberReset}
        />
      ) : null}
    </>
  );
}

// The team's roster, hung off a hairline dropped from the centre of the
// team's mark, each row one small step to the right of the line and one
// rung tighter. A member reads the team's tier in muted ink and carries the
// same tier menu and switch as any row. Picking a tier or flipping the
// switch writes a share of that person's own, which beats the team's row
// in both directions, and the menu grows "Reset to team" to clear it. The
// override stays here, under the team — a person appears once.
function TeamMemberRows({
  source,
  teamId,
  memberCount,
  memberLabel,
  onChange,
  onDeny,
  onReset,
}: {
  source: TeamMembersSource;
  teamId: string;
  // The team row already knows its size, so the skeleton renders exactly
  // this many rows: the block lands at its settled height on the first
  // frame and the band never grows past it and shrinks back.
  memberCount: number;
  memberLabel: (m: AccessSummaryMember) => string;
  onChange: (id: string, level: ObjectAccessLevel) => void;
  onDeny: (id: string, level: ObjectAccessLevel) => void;
  onReset: (id: string) => void;
}) {
  const { members, loading } = useTeamAccessMembers(source, teamId, true);
  return (
    <Box direction="row">
      <Box border="right" shrink={false} width={ACCESS_VISUAL_PX / 2} />
      <Box flex={1} minWidth={0} paddingLeft="sm">
        {loading ? (
          <SkeletonGroup>
            {Array.from({ length: Math.max(1, memberCount) }, (_, i) => (
              <SkeletonRow key={i} height={NESTED_ROW_HEIGHT} />
            ))}
          </SkeletonGroup>
        ) : members.length === 0 ? (
          <Box height={NESTED_ROW_HEIGHT} justify="center">
            <Text size="xs" variant="muted">
              No members
            </Text>
          </Box>
        ) : (
          members.map((member) => {
            const custom = member.source === "shared";
            return (
              <AccessRowFrame
                key={member.id}
                entity={memberEntity(member)}
                label={memberLabel(member)}
                role={roleBadge(member)}
                meta={member.locked ? undefined : memberMeta(member)}
                height={NESTED_ROW_HEIGHT}
                dimmed={!member.enabled}
              >
                {member.locked ? (
                  <LockedLevel />
                ) : (
                  <>
                    <AccessLevelMenu
                      value={member.accessLevel}
                      inherited={!custom}
                      disabled={!member.enabled}
                      onChange={(level) => onChange(member.id, level)}
                      onReset={custom ? () => onReset(member.id) : undefined}
                    />
                    <Switch
                      aria-label={`${memberLabel(member)} has access`}
                      checked={member.enabled}
                      // Off writes a deny of the member's own; on clears
                      // it, so the team's path reaches them again.
                      onCheckedChange={(on) =>
                        on
                          ? onReset(member.id)
                          : onDeny(member.id, member.accessLevel)
                      }
                      size="sm"
                    />
                  </>
                )}
              </AccessRowFrame>
            );
          })
        )}
      </Box>
    </Box>
  );
}
